import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Shield, Lock, Database, UserCheck, Activity, ArrowRight, CheckCircle, Globe, Server, Eye } from 'lucide-react';

const securityFeatures = [
  {
    text: 'OpenAI Enterprise API – Your data is never used to train AI models, ensuring compliance and secure AI document processing.',
    icon: Shield
  },
  {
    text: 'AWS Encryption Standards – All data is encrypted at rest and in transit using AES-256 encryption for maximum protection.',
    icon: Lock
  },
  {
    text: 'Dedicated Data Isolation – Each client\'s information is stored in separate AWS S3 buckets, ensuring secure document management for enterprises.',
    icon: Database
  },
  {
    text: 'Secure Access Controls – Role-based permissions, MFA, and logged access give you compliance-ready audit trails for regulatory industries.',
    icon: UserCheck
  },
  {
    text: 'High Availability & Reliability – With AWS multi-AZ infrastructure, Ufficient delivers 99.99% uptime, ensuring projects and legal teams never lose access.',
    icon: Activity
  }
];

const trustBadges = [
  { title: '99.99%', subtitle: 'Uptime SLA', icon: Activity },
  { title: 'AES-256', subtitle: 'Encryption', icon: Lock },
  { title: 'SOC 2', subtitle: 'Ready', icon: Shield },
  { title: 'GDPR', subtitle: 'Ready', icon: Globe }
];

const SecurityTrustShowcase = () => {
  return (
    <section className="py-24 bg-gradient-to-br from-slate-900 via-gray-900 to-blue-900 text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-4"
          >
            <span className="text-blue-400 font-semibold text-sm sm:text-base uppercase tracking-wide">
              Enterprise-Grade Security
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl font-bold mb-8 leading-tight"
          >
            Your Data, Protected with <span className="text-blue-400">World-Class Security</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed"
          >
            At Ufficient, security isn't an add-on, it's built into our foundation. Leveraging AWS encryption, OpenAI enterprise APIs, and isolated cloud storage, we provide enterprise-grade protection for construction, engineering, and legal professionals managing sensitive documents and compliance-critical workflows.
          </motion.p>
        </div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-20"
        >
          <div className="grid md:grid-cols-4 gap-8">
            {trustBadges.map((badge, index) => {
              const Icon = badge.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center group hover:bg-white/15 transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-200">
                    <Icon size={32} className="text-blue-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">{badge.title}</h3>
                  <p className="text-gray-400 text-sm font-medium">{badge.subtitle}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Security Features Grid
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-20"
        >
          <div className="grid lg:grid-cols-2 gap-8">
            {securityFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300 group"
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                      <Icon size={24} className="text-blue-400" />
                    </div>
                    <p className="text-gray-200 leading-relaxed flex-1">{feature.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div> */}

        {/* Security Architecture Visual */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mb-20"
        >
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-white/10">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-white mb-4">Zero-Trust Security Architecture</h3>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Multi-layered security ensures your sensitive data remains protected at every level
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Server size={40} className="text-blue-400" />
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">Infrastructure</h4>
                <p className="text-gray-400 text-sm">AWS multi-AZ deployment with dedicated VPCs and isolated storage</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock size={40} className="text-blue-400" />
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">Encryption</h4>
                <p className="text-gray-400 text-sm">End-to-end AES-256 encryption with TLS 1.3 for data in transit</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Eye size={40} className="text-blue-400" />
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">Monitoring</h4>
                <p className="text-gray-400 text-sm">24/7 threat detection with complete audit logs and compliance reporting</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Compliance & Certifications */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mb-16"
        >
          <h3 className="text-2xl font-bold text-white mb-8">Built for Regulatory Compliance</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              'GDPR Ready',
              'SOC 2 Type II Ready',
              'ISO 27001 Ready',
              'HIPAA Compatible'
            ].map((cert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:bg-white/15 transition-all duration-300"
              >
                <CheckCircle size={24} className="text-green-400 mx-auto mb-2" />
                <p className="text-white font-medium text-sm">{cert}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="text-center"
        >
          <p className="text-lg text-gray-300 mb-8">
            Ready to secure your organization's most sensitive data?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/trust">
              <button className="inline-flex items-center px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1 group">
                View Protections
                <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
            <Link to="/contact">
              <button className="inline-flex items-center px-8 py-4 bg-white/10 border border-white/20 hover:bg-white/15 text-white rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1 group">
                Security Assessment
                <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SecurityTrustShowcase; 